// import AlertsContext from 'common/contexts/alerts';
import useRouter from 'common/hooks/use-router';
import useItem from 'common/hooks/useItem';
import useList from 'common/hooks/useList';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ListBulletIcon, PencilIcon, UserIcon, UsersIcon } from '@heroicons/react/24/outline';
import Header from 'common/components/Header/Header';

import FilteredList from 'common/components/Table/FilteredList';
import useAuth from 'common/hooks/useAuth';
import RoleSelect from 'components/select/RoleSelect/RoleSelect';
import AlertsContext from 'common/providers/alerts';
import UserSearch from 'components/search/UserSearch';
import { User } from 'types';
import ButtonGroup from 'common/components/ButtonGroup/ButtonGroup';
import UserItem from './UserItem';
import { RiAdminLine, RiUserStarLine } from 'react-icons/ri';
import RadioBlock from 'common/components/RadioBlock/RadioBlock';
import TabBlockItem from 'common/components/TabBlockItem/TabBlockItem';

function Users() {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { setAlert } = useContext(AlertsContext);

  const { user: currentUser } = useAuth();
  const canManageUsers = currentUser?.isAdmin;

  const {
    fetchItems,
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    removeListItem,
    itemsCount,
    itemsPerPage,
    error,
  } = useList<User>('users', {
    defaultSort: 'createdAt:DESC',
    populate: ['profiles', 'operator', 'role'],
    cachePrefix: 'Users',
  });

  useEffect(() => {
    if (error) {
      setAlert(error, 'danger');
    }
  }, [error, setAlert]);

  const {
    fetchItems: fetchItemsCount,
    items: usersCount,
    isFetching: isFetchingCount,
    error: errorCount,
  } = useList<User>('users', {
    defaultSort: 'createdAt:DESC',
    populate: ['role'],
    cachePrefix: 'UsersCount',
  });

  useEffect(() => {
    if (errorCount) {
      setAlert(errorCount, 'danger');
    }
  }, [errorCount, setAlert]);

  const statusList = useMemo(() => {
    return [
      {
        id: '',
        name: t('status.all'),
        count: usersCount?.length || 0,
        icon: ListBulletIcon,
        color: 'primary',
      },
      {
        id: 'Authenticated',
        name: t('roles.Authenticated'),
        count: usersCount?.filter((user) => user.role?.name === 'Authenticated').length || 0,
        icon: UserIcon,
        color: 'violet',
      },
      {
        id: 'Mediator',
        name: t('roles.Mediator'),
        count: usersCount?.filter((user) => user.role?.name === 'Mediator').length || 0,
        icon: RiUserStarLine,
        color: 'green',
      },
      {
        id: 'Admin',
        name: t('roles.Administrateur'),
        count:
          usersCount?.filter(
            (user) => user.role?.name === 'Admin' || user.role?.name === 'SuperAdmin'
          ).length || 0,
        icon: RiAdminLine,
        color: 'slate',
      },
    ];
  }, [t, usersCount]);

  const { removeItem } = useItem<User>('users', '');

  const handleDelete = useCallback(
    async (id: any) => {
      try {
        removeListItem(id);
        await removeItem(id);
        fetchItems();
      } catch (e: any) {
        setAlert(e, 'danger');
        return e;
      }
      return true;
    },
    [removeListItem, fetchItems, removeItem, setAlert]
  );

  const handleNavigate = (id: any) => {
    navigate(`/users/edit/${id}`);
  };

  const styleTop = 'md:w-1/3 xl:flex-1 xl:w-auto';
  const styleBottom = 'md:w-1/3 mt-2 xl:flex-1 xl:w-auto xl:mt-0';

  const showRoles = canManageUsers;

  const columns = useMemo(
    () => [
      {
        Header: t('filters.search'),
        filterName: 'username$containsi',
        columnClasses: styleTop,
        filterLabel: t('filters.search'),
        filterComponent: (props: any) => <UserSearch isFilter {...props} />,
      },
      {
        Header: t('common.verifiedEmail'),
        filterName: 'confirmed',
        filterComponent: (props: any) => (
          <ButtonGroup
            className="w-full"
            options={[
              { value: null, label: t('status.all') },
              { value: true, label: t('status.mailValidated') },
              { value: false, label: t('status.mailNotValidated') },
            ]}
            {...props}
          />
        ),
      },
      {
        Header: t('forms.blocked'),
        filterName: 'blocked',
        filterComponent: (props: any) => (
          <ButtonGroup
            className="w-full"
            options={[
              { value: null, label: t('status.all') },
              { value: false, label: t('status.active') },
              { value: true, label: t('status.blocked') },
            ]}
            {...props}
          />
        ),
      },
      {
        Header: t('forms.noProfile'),
        filterName: 'operator][id][$null]=true&filters[profiles][id][$null',
        filterComponent: (props: any) => (
          <ButtonGroup
            className="w-full"
            options={[
              { value: null, label: t('status.all') },
              { value: true, label: t('status.noProfile') },
            ]}
            {...props}
          />
        ),
      },
    ],
    [t, showRoles]
  );

  return (
    <div className="mx-auto max-w-full py-6 px-6 text-slate-500">
      <Header
        title={t('titlePages.users')}
        subTitle={t('titleLegends.users')}
        btnTitle={t('newItem.user')}
        btnSubtitle={t('newItem.userLegend')}
        Icon={UsersIcon}
        addLink="/users/add"
        addLinkDisabled={!canManageUsers}
        isBeforeFilters
        filters={filters}
        setFilter={setFilter}
        filterName="role][name$containsi"
        FilterComponent={(props: any) => (
          <RadioBlock Component={TabBlockItem} items={statusList} horizontal noGap {...props} />
        )}
      />

      <FilteredList
        data={items ?? []}
        columns={columns} // used as filter items
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={UserItem}
        onDelete={handleDelete}
        onNavigate={handleNavigate}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default Users;
